/**
 * Shared utility functions for election cycle parsing and validation.
 * This module is designed to be imported by other modules without creating circular dependencies.
 */

/**
 * Get the current year for election cycle calculations.
 * @returns {number} - The current year
 */
export const getCurrentElectionCycleYear = () => {
    return new Date().getFullYear()
}

/**
 * Parse election cycle value to get start and end years.
 * Election cycles always end in even years (e.g., 2019-2020, 2021-2022).
 * @param {string} cycleValue - The election cycle value (e.g., "2023-2024" or "all-cycles")
 * @returns {Object|null} - Object with startYear and endYear properties, or null if invalid
 */
export const parseElectionCycle = (cycleValue) => {
    if (!cycleValue) return null

    if (cycleValue === "all-cycles") {
        const currentYear = getCurrentElectionCycleYear()
        // Ensure end year is even
        const endYear = currentYear % 2 === 0 ? currentYear + 2 : currentYear + 1
        return {
            startYear: 2015,
            endYear: endYear,
        }
    }

    // Validate format before parsing
    const parts = cycleValue.split("-")
    if (parts.length !== 2) {
        console.warn(`Invalid election cycle format: ${cycleValue}. Expected format: YYYY-YYYY`)
        return null
    }

    const [startYear, endYear] = parts.map(Number)
    if (isNaN(startYear) || isNaN(endYear)) {
        console.warn(`Invalid election cycle years: ${cycleValue}. Years must be numeric.`)
        return null
    }

    // Validate that end year is even
    if (endYear % 2 !== 0) {
        console.warn(`Invalid election cycle: ${cycleValue}. End year must be even.`)
        return null
    }

    return { startYear, endYear }
}
